import { Mesh, Object3D, Vector3 } from 'three'

interface Options {
  /** Already-batched models need all surfaces to retain their silhouette. */
  preserveGeometry?: boolean
  /** Distance where fine fittings and small repeated details are hidden. */
  detailDistance?: number
  /** Distance where shadow map work is disabled for the model. */
  shadowDistance?: number
}

interface MeshRecord {
  mesh: Mesh
  fine: boolean
  micro: boolean
  cargo: boolean
  visible: boolean
  castShadow: boolean
  receiveShadow: boolean
}

interface Entry {
  root: Object3D
  options: Required<Options>
  meshes: MeshRecord[]
  refreshAt: number
  level: -1 | 0 | 1 | 2
}

const _worldPosition = new Vector3()

// Blender-authored detail that reads beautifully close up but contributes a
// large number of tiny triangles when a vessel is only a few pixels tall.
const FINE_DETAIL = /corrug|lashing|casting|paint wear|rust runoff|weld|rail|hinge|seal|socket|joint|chevron|chain|ladder|pipe|handle|lock/i
const MICRO_DETAIL = /bolt|nut|screw|slot|grille|mesh|stitch|plate seam/i

/** Distance-based quality policy shared by every authored model in the game. */
export class RuntimeModelDetail {
  private readonly entries = new Map<Object3D, Entry>()

  track(root: Object3D, options: Options = {}): void {
    const existing = this.entries.get(root)
    if (existing) return
    this.entries.set(root, {
      root,
      options: { preserveGeometry: options.preserveGeometry ?? false, detailDistance: options.detailDistance ?? 320, shadowDistance: options.shadowDistance ?? 560 },
      meshes: [],
      refreshAt: -Infinity,
      level: -1,
    })
  }

  update(cameraPosition: Vector3, elapsed: number): void {
    for (const [root, entry] of this.entries) {
      // Mode changes remove their roots from the scene. Pruning here also keeps
      // a replaced barge or vessel from retaining its mesh records.
      if (!root.parent) {
        this.entries.delete(root)
        continue
      }
      if (elapsed >= entry.refreshAt) {
        this.refresh(entry)
        entry.refreshAt = elapsed + .5
      }
      root.getWorldPosition(_worldPosition)
      const distance = _worldPosition.distanceTo(cameraPosition)
      const nextLevel: -1 | 0 | 1 | 2 = distance > entry.options.shadowDistance
        ? 2
        : distance > entry.options.detailDistance
          ? 1
          : 0
      if (nextLevel === entry.level) continue
      entry.level = nextLevel
      for (const record of entry.meshes) {
        // Container stacks are the largest repeated batches in the barge and
        // harbour exports. At the far chase distances their silhouette is
        // negligible, so dropping those batches removes a lot of triangles and
        // fragment work while preserving the hull and dock structures.
        record.mesh.visible = record.visible && (entry.options.preserveGeometry || (
          !(nextLevel >= 1 && record.fine)
          && !(nextLevel >= 2 && record.micro)
          && !(nextLevel >= 2 && record.cargo)
        ))
        record.mesh.castShadow = record.castShadow && distance <= entry.options.shadowDistance
        record.mesh.receiveShadow = record.receiveShadow && distance <= entry.options.shadowDistance * 1.35
      }
    }
  }

  private refresh(entry: Entry): void {
    // Retain authored states: current flags may already be reduced by LOD.
    const previous = new Map(entry.meshes.map(record => [record.mesh, record]))
    entry.meshes = []
    entry.root.traverse(object => {
      if (!(object as Mesh).isMesh) return
      const mesh = object as Mesh
      const existing = previous.get(mesh)
      if (existing) {
        entry.meshes.push(existing)
        return
      }
      const name = mesh.name
      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material]
      const materialNames = materials.map(material => material.name).join(' ')
      entry.meshes.push({
        mesh,
        fine: FINE_DETAIL.test(name),
        micro: MICRO_DETAIL.test(name),
        cargo: /cargo|container|yard container/i.test(`${name} ${materialNames}`),
        visible: mesh.visible,
        castShadow: mesh.castShadow,
        receiveShadow: mesh.receiveShadow,
      })
    })
    // A newly loaded model must be re-evaluated even if the distance did not
    // change since the previous refresh.
    entry.level = -1
  }
}
